import { client } from "@/client";
import { CustomToastComponent } from "@/components/shared";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useMutation } from "@apollo/client/react";
import {
  RemoveShiftMutation,
  RemoveShiftMutationVariables,
  RemoveShiftDocument,
  FetchTenderByIdDocument,
  FetchServicesOnTenderDocument,
} from "@/graphql/__generated__/graphql";

interface RemoveShiftVariables {
  serviceId: number;
  shiftId: number;
}

/**
 * Custom hook for removing a shift from a service and handling the necessary cache updates.
 * @param {number} _tenderId - The ID of the tender to be updated in the cache
 * @returns {Object}
 */
export default function useRemoveShiftMutationHook(_tenderId: number): {
  removeShiftMutation: (
    _variables: RemoveShiftVariables,
    _isEditShift?: boolean
  ) => Promise<any>;
  loading: boolean;
  error: any;
} {
  const { t } = useTranslation(["toasts"]);

  const [removeShiftMutation, { loading, error }] = useMutation<RemoveShiftMutation, RemoveShiftMutationVariables>(RemoveShiftDocument);

  // Simple wrapper that accepts isEditShift as a direct parameter
  const removeShift = (
    variables: RemoveShiftVariables,
    isEditShift: boolean = false
  ) => {
    return removeShiftMutation({
      variables,
      onCompleted: () => {
        // Remove Shift from cache
        client.cache.evict({
          id: `Shift:${variables.shiftId}`,
        });

        if (!isEditShift) {
          toast(CustomToastComponent, {
            toastId: "shift-removed-success",
            data: {
              title: t("toasts.shiftremoved"),
              content: t("toasts.shiftremovedsuccessfully"),
              type: "success",
            },
          });
        }
      },
      refetchQueries: isEditShift
        ? []
        : [
          {
            query: FetchTenderByIdDocument,
            variables: { id: _tenderId },
          },
          {
            query: FetchServicesOnTenderDocument,
            variables: { tenderId: Number(_tenderId) },
          },
        ],
    });
  };

  return {
    removeShiftMutation: removeShift,
    loading,
    error,
  };
}
